import 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { VisuallyHidden } from '@thisisbud/bui-react-visually-hidden';
import { jsxs, jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t;
const iconClassName = 'bui-icon';
const iconSize = "1.5em";

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledSpan = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledSpan",
  componentId: "sc-poh1q7-0"
})(_t || (_t = _`
  display: inline-block;
  vertical-align: text-bottom;

  svg {
    display: block;
    width: ${0};
    height: ${0};
  }
`), iconSize, iconSize);

/**
 * Renders the container of an icon.
 *
 * @remarks This component is for internal use only
 * @param props - The component props
 */
function Icon(props) {
  const {
    children,
    className,
    label
  } = props;
  return /*#__PURE__*/jsxs(StyledSpan, {
    className: classnames(iconClassName, className),
    children: [typeof label === 'string' ? /*#__PURE__*/jsx(VisuallyHidden, {
      children: label
    }) : null, /*#__PURE__*/jsx("svg", {
      "aria-hidden": "true",
      role: "img",
      viewBox: "0 0 24 24",
      xmlns: "http://www.w3.org/2000/svg",
      children: children
    })]
  });
}
Icon.displayName = 'Icon';

var _g$l;
const approvedIconClassName = 'bui-approved-icon';

/**
 * A component that displays an approved icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ApprovedIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ApprovedIcon/>
 * }
 * ```
 */
function ApprovedIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(approvedIconClassName, className),
    label: label,
    children: _g$l || (_g$l = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [/*#__PURE__*/jsx("path", {
        d: "m15 10.2-3.18 4.25a.6.6 0 0 1-.9.06L9 12.6"
      }), /*#__PURE__*/jsx("path", {
        d: "M10.99 3.49a1.3 1.3 0 0 1 2.03 0l1.21 1.5a1.3 1.3 0 0 0 1.16.48l1.92-.2a1.3 1.3 0 0 1 1.44 1.43l-.21 1.92a1.3 1.3 0 0 0 .48 1.16l1.5 1.2a1.3 1.3 0 0 1 0 2.04l-1.5 1.2a1.3 1.3 0 0 0-.49 1.16l.21 1.92a1.3 1.3 0 0 1-1.44 1.44l-1.92-.2a1.3 1.3 0 0 0-1.15.47l-1.2 1.5a1.3 1.3 0 0 1-2.04 0l-1.21-1.5a1.3 1.3 0 0 0-1.16-.48l-1.92.2a1.3 1.3 0 0 1-1.44-1.43l.21-1.92A1.3 1.3 0 0 0 5 14.22l-1.5-1.2a1.3 1.3 0 0 1 0-2.04L5 9.77a1.3 1.3 0 0 0 .48-1.16l-.2-1.92A1.3 1.3 0 0 1 6.7 5.25l1.92.21a1.3 1.3 0 0 0 1.16-.48l1.2-1.5Z"
      })]
    }))
  });
}
ApprovedIcon.displayName = 'ApprovedIcon';

var _path$B;
const arrowDownIconClassName = 'bui-arrow-down-icon';

/**
 * A component that displays an arrow pointing down icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ArrowDownIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ArrowDownIcon/>
 * }
 * ```
 */
function ArrowDownIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(arrowDownIconClassName, className),
    label: label,
    children: _path$B || (_path$B = /*#__PURE__*/jsx("path", {
      d: "M16 14.7h-3V5h-2v9.7H8l4 4.3 4-4.3z",
      fill: "currentColor"
    }))
  });
}
ArrowDownIcon.displayName = 'ArrowDownIcon';

var _path$A;
const arrowDownUpIconClassName = 'bui-arrow-down-up-icon';

/**
 * A component that displays an arrow pointing down icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ArrowDownUpIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ArrowDownUpIcon/>
 * }
 * ```
 */
function ArrowDownUpIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(arrowDownUpIconClassName, className),
    label: label,
    children: _path$A || (_path$A = /*#__PURE__*/jsx("path", {
      d: "M15 9.3V19h2V9.3h3L16 5l-4 4.3h3zM12 14.7H9V5H7v9.7H4L8 19l4-4.3z",
      fill: "currentColor"
    }))
  });
}
ArrowDownUpIcon.displayName = 'ArrowDownUpIcon';

var _path$z;
const arrowUpIconClassName = 'bui-arrow-up-icon';

/**
 * A component that displays an arrow pointing up icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ArrowUpIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ArrowUpIcon/>
 * }
 * ```
 */
function ArrowUpIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(arrowUpIconClassName, className),
    label: label,
    children: _path$z || (_path$z = /*#__PURE__*/jsx("path", {
      d: "M8 9.3h3V19h2V9.3h3L12 5 8 9.3z",
      fill: "currentColor"
    }))
  });
}
ArrowUpIcon.displayName = 'ArrowUpIcon';

var _path$y, _stop, _stop2, _stop3, _stop4, _stop5, _stop6, _stop7, _stop8, _stop9;
const azureIconClassName = 'bui-azure-icon';

/**
 * A component that displays an Azure logo icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { AzureIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <AzureIcon/>
 * }
 * ```
 */
function AzureIcon(props) {
  const {
    className,
    id,
    label
  } = props;
  return /*#__PURE__*/jsxs(Icon, {
    className: classnames(azureIconClassName, className),
    label: label,
    children: [/*#__PURE__*/jsx("path", {
      d: "M9.14 2.404h5.967L8.913 20.942a.96.96 0 0 1-.347.474.945.945 0 0 1-.555.18H3.367a.941.941 0 0 1-.771-.4.965.965 0 0 1-.129-.866L8.238 3.057a.96.96 0 0 1 .347-.473.945.945 0 0 1 .555-.18Z",
      fill: `url(#${id}-gradient-a)`
    }), _path$y || (_path$y = /*#__PURE__*/jsx("path", {
      d: "M17.81 14.838H8.35a.435.435 0 0 0-.408.28.447.447 0 0 0 .109.486l6.08 5.733a.95.95 0 0 0 .652.26h5.358l-2.33-6.759Z",
      fill: "#0078D4"
    })), /*#__PURE__*/jsx("path", {
      d: "M9.14 2.404a.937.937 0 0 0-.56.183.953.953 0 0 0-.344.483L2.474 20.314a.967.967 0 0 0 .458 1.175c.135.07.285.107.438.107h4.763a1.015 1.015 0 0 0 .782-.671l1.149-3.42 4.104 3.866a.966.966 0 0 0 .611.225h5.338l-2.34-6.758-6.826.002 4.177-12.436H9.14Z",
      fill: `url(#${id}-gradient-b)`
    }), /*#__PURE__*/jsx("path", {
      d: "M16.761 3.056a.96.96 0 0 0-.346-.472.943.943 0 0 0-.554-.18h-6.65c.199 0 .393.063.554.18a.96.96 0 0 1 .346.472l5.771 17.274a.969.969 0 0 1-.128.866.954.954 0 0 1-.772.4h6.65a.943.943 0 0 0 .772-.4.964.964 0 0 0 .129-.866L16.76 3.056Z",
      fill: `url(#${id}-gradient-c)`
    }), /*#__PURE__*/jsxs("defs", {
      children: [/*#__PURE__*/jsxs("linearGradient", {
        gradientUnits: "userSpaceOnUse",
        id: `${id}-gradient-a`,
        x1: "11.315",
        x2: "5.005",
        y1: "3.826",
        y2: "22.279",
        children: [_stop || (_stop = /*#__PURE__*/jsx("stop", {
          stopColor: "#114A8B"
        })), _stop2 || (_stop2 = /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopColor: "#0669BC"
        }))]
      }), /*#__PURE__*/jsxs("linearGradient", {
        gradientUnits: "userSpaceOnUse",
        id: `${id}-gradient-b`,
        x1: "13.25",
        x2: "11.814",
        y1: "12.444",
        y2: "12.925",
        children: [_stop3 || (_stop3 = /*#__PURE__*/jsx("stop", {
          stopOpacity: ".3"
        })), _stop4 || (_stop4 = /*#__PURE__*/jsx("stop", {
          offset: ".071",
          stopOpacity: ".2"
        })), _stop5 || (_stop5 = /*#__PURE__*/jsx("stop", {
          offset: ".321",
          stopOpacity: ".1"
        })), _stop6 || (_stop6 = /*#__PURE__*/jsx("stop", {
          offset: ".623",
          stopOpacity: ".05"
        })), _stop7 || (_stop7 = /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopOpacity: "0"
        }))]
      }), /*#__PURE__*/jsxs("linearGradient", {
        gradientUnits: "userSpaceOnUse",
        id: `${id}-gradient-c`,
        x1: "12.462",
        x2: "19.385",
        y1: "3.287",
        y2: "21.547",
        children: [_stop8 || (_stop8 = /*#__PURE__*/jsx("stop", {
          stopColor: "#3CCBF4"
        })), _stop9 || (_stop9 = /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopColor: "#2892DF"
        }))]
      })]
    })]
  });
}
AzureIcon.displayName = 'AzureIcon';

var _path$x;
const banknoteIconClassName = 'bui-banknote-icon';

/**
 * A component that displays a banknote icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { BanknoteIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <BanknoteIcon/>
 * }
 * ```
 */
function BanknoteIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(banknoteIconClassName, className),
    label: label,
    children: _path$x || (_path$x = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M3.2 18.6c-.78 0-1.4-.66-1.4-1.46V6.86c0-.8.62-1.46 1.4-1.46h17.6c.78 0 1.4.66 1.4 1.46v10.28c0 .8-.62 1.46-1.4 1.46H3.2zm0-1.2h17.6c.1 0 .2-.1.2-.26V6.86c0-.15-.1-.26-.2-.26H3.2c-.1 0-.2.1-.2.26v10.28c0 .15.1.26.2.26zm10.52-3.28a3 3 0 10-4.24-4.24 3 3 0 004.24 4.24zm-3.4-3.4a1.8 1.8 0 112.55 2.55 1.8 1.8 0 01-2.55-2.54z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
BanknoteIcon.displayName = 'BanknoteIcon';

var _g$k;
const binIconClassName = 'bui-bin-icon';

/**
 * A component that displays a bin icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { BinIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <BinIcon/>
 * }
 * ```
 */
function BinIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(binIconClassName, className),
    label: label,
    children: _g$k || (_g$k = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [/*#__PURE__*/jsx("path", {
        d: "M16.85 20h-9.7c-.36 0-.72-.15-.97-.41a1.45 1.45 0 0 1-.41-1V5.82h12.46v12.75a1.4 1.4 0 0 1-1.38 1.42Zm-6.93-4.25v-5.67m4.16 5.67v-5.67M3 5.83h18"
      }), /*#__PURE__*/jsx("path", {
        d: "M14.08 3H9.92c-.36 0-.72.15-.98.41-.26.27-.4.63-.4 1v1.42h6.92V4.42c0-.38-.14-.74-.4-1a1.37 1.37 0 0 0-.98-.42Z"
      })]
    }))
  });
}
BinIcon.displayName = 'BinIcon';

var _path$w;
const burgerIconClassName = 'bui-burger-icon';

/**
 * A component that displays a burger icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { BurgerIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <BurgerIcon/>
 * }
 * ```
 */
function BurgerIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(burgerIconClassName, className),
    label: label,
    children: _path$w || (_path$w = /*#__PURE__*/jsx("path", {
      d: "M4 17h16.25M4 12h16.25M4 7h16.25",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5"
    }))
  });
}
BurgerIcon.displayName = 'BurgerIcon';

var _path$v;
const busIconClassName = 'bui-bus-icon';

/**
 * A component that displays a bus icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { BusIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <BusIcon/>
 * }
 * ```
 */
function BusIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(busIconClassName, className),
    label: label,
    children: _path$v || (_path$v = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M4.2 17.56a2.27 2.27 0 002.3 2.24h.1v1c0 .79.69 1.4 1.5 1.4s1.5-.61 1.5-1.4v-1h4.8v1c0 .79.69 1.4 1.5 1.4s1.5-.61 1.5-1.4v-1h.1c1.27 0 2.3-1 2.3-2.24V10.8h1c.78 0 1.4-.65 1.4-1.44V6.84c0-.79-.62-1.44-1.4-1.44h-1v-.76a2.27 2.27 0 00-2.3-2.24h-11c-1.27 0-2.3 1-2.3 2.24v.76h-1c-.78 0-1.4.65-1.4 1.44v2.52c0 .79.62 1.44 1.4 1.44h1v6.76zM19.8 6.6h1c.1 0 .2.1.2.24v2.52c0 .14-.1.24-.2.24h-1v-3zm-1.2 3.6V4.64c0-.57-.49-1.04-1.1-1.04h-11c-.61 0-1.1.47-1.1 1.04v8.96h13.2v-3.4zM5.4 17.56V14.8h13.2v2.76c0 .57-.49 1.04-1.1 1.04h-11c-.61 0-1.1-.47-1.1-1.04zM3.2 6.6h1v3h-1c-.1 0-.2-.1-.2-.24V6.84c0-.14.1-.24.2-.24zm13 13.2v1c0 .1-.12.2-.3.2s-.3-.1-.3-.2v-1h.6zm-8.4 1v-1h.6v1c0 .1-.12.2-.3.2s-.3-.1-.3-.2zm-1.2-4.6c0 .33.27.6.6.6h1.2a.6.6 0 100-1.2H7.2a.6.6 0 00-.6.6zm9 .6a.6.6 0 110-1.2h1.2a.6.6 0 010 1.2h-1.2zM9 5.4c0 .33.27.6.6.6h4.8a.6.6 0 100-1.2H9.6a.6.6 0 00-.6.6z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
BusIcon.displayName = 'BusIcon';

var _path$u;
const calloutIconClassName = 'bui-callout-icon';

/**
 * A component that displays a callout icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { CalloutIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <CalloutIcon/>
 * }
 * ```
 */
// TODO: Remove CalloutIcon from library as it is a duplicate of the warning icon
function CalloutIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(calloutIconClassName, className),
    label: label,
    children: _path$u || (_path$u = /*#__PURE__*/jsx("path", {
      d: "M5.1 5.1a9.76 9.76 0 1 0 13.8 13.8A9.75 9.75 0 0 0 5.12 5.1zm6.9 9.16c.52 0 .94-.46.94-1.03V7.04c0-.57-.42-1.03-.94-1.03s-.94.46-.94 1.03v6.19c0 .57.42 1.03.94 1.03zm-.98 2.58a.99.99 0 1 1 1.97 0 .99.99 0 0 1-1.97 0zm0 0",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
CalloutIcon.displayName = 'CalloutIcon';

var _g$j;
const cashIconClassName = 'bui-cash-icon';

/**
 * A component that displays a cash icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { CashIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <CashIcon/>
 * }
 * ```
 */
function CashIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(cashIconClassName, className),
    label: label,
    children: _g$j || (_g$j = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.3",
      children: [/*#__PURE__*/jsx("path", {
        d: "M7 12c2.2 0 4-.45 4-1s-1.8-1-4-1-4 .45-4 1 1.8 1 4 1z"
      }), /*#__PURE__*/jsx("path", {
        d: "M11 11v2.25c0 .41-1.79.75-4 .75s-4-.34-4-.75V11"
      }), /*#__PURE__*/jsx("path", {
        d: "M11 13v2.63c0 .48-1.79.87-4 .87s-4-.4-4-.88V13"
      }), /*#__PURE__*/jsx("path", {
        d: "M11 16v2.25c0 .41-1.79.75-4 .75s-4-.34-4-.75V16"
      }), /*#__PURE__*/jsx("path", {
        d: "M11 18v2.25c0 .41-1.79.75-4 .75s-4-.34-4-.75V18M3 8.5V3.79A.79.79 0 013.78 3h16.44a.78.78 0 01.78.79v9.42a.79.79 0 01-.78.79h-7.44"
      }), /*#__PURE__*/jsx("path", {
        d: "M10 8.57a2.53 2.53 0 011.35-2.29 2.44 2.44 0 012.6.2 2.64 2.64 0 01.5 3.73 2.5 2.5 0 01-1.12.79"
      })]
    }))
  });
}
CashIcon.displayName = 'CashIcon';

var _g$i;
const cashWadIconClassName = 'bui-cash-wad-icon';

/**
 * A component that displays a cash wad icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { CashWadIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <CashWadIcon/>
 * }
 * ```
 */
function CashWadIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(cashWadIconClassName, className),
    label: label,
    children: _g$i || (_g$i = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.3",
      children: [/*#__PURE__*/jsx("path", {
        d: "M17.42 5.33H3.25a.83.83 0 00-.83.84v8.33c0 .46.37.83.83.83h14.17c.46 0 .83-.37.83-.83V6.17a.83.83 0 00-.83-.84z"
      }), /*#__PURE__*/jsx("path", {
        d: "M19.92 7v9.17a.83.83 0 01-.84.83h-15"
      }), /*#__PURE__*/jsx("path", {
        d: "M21.58 8.67v9.16a.83.83 0 01-.83.84h-15M10.34 12.83a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
      })]
    }))
  });
}
CashWadIcon.displayName = 'CashWadIcon';

var _path$t;
const checkmarkIconClassName = 'bui-checkmark-icon';

/**
 * A component that displays a checkmark icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { CheckmarkIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <CheckmarkIcon/>
 * }
 * ```
 */
function CheckmarkIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(checkmarkIconClassName, className),
    label: label,
    children: _path$t || (_path$t = /*#__PURE__*/jsx("path", {
      d: "M5.53 13.86a1 1 0 010-1.39 1 1 0 011.37 0l2.39 2.4L17.09 7a1 1 0 011.38 1.39L10 17a1 1 0 01-1.38 0z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
CheckmarkIcon.displayName = 'CheckmarkIcon';

var _path$s;
const closeIconClassName = 'bui-close-icon';

/**
 * A component that displays a close icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { CloseIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <CloseIcon/>
 * }
 * ```
 */
function CloseIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(closeIconClassName, className),
    label: label,
    children: _path$s || (_path$s = /*#__PURE__*/jsx("path", {
      d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z",
      fill: "currentColor"
    }))
  });
}
CloseIcon.displayName = 'CloseIcon';

var _g$h;
const contractHandshakeIconClassName = 'bui-contract-handshake-icon';

/**
 * A component that displays a contract handshake icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ContractHandshakeIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ContractHandshakeIcon/>
 * }
 * ```
 */
function ContractHandshakeIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(contractHandshakeIconClassName, className),
    label: label,
    children: _g$h || (_g$h = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: ".66666",
      children: [/*#__PURE__*/jsx("path", {
        d: "M20 20.65l-3.93.79M16.5 17l-1.86.76c-.28.12-.6.12-.89 0a1.22 1.22 0 01-.65-.62 1.2 1.2 0 01.56-1.57l1.86-.9a1.7 1.7 0 011.3-.07l3.39 1.3"
      }), /*#__PURE__*/jsx("path", {
        d: "M9 21.5h1.4l2.33 1.77c.12.13.28.21.46.23.17.01.35-.04.49-.16l3.5-2.8a.69.69 0 00.1-.95l-1.92-2.12M13.51 15.68l-.25-.21a1.49 1.49 0 00-1.42-.18L9 16.5M6 22.5h2a1 1 0 001-1v-5a1 1 0 00-1-1H6zm0 0M23 22.5h-2a1 1 0 01-1-1v-5a1 1 0 011-1h2zm0 0"
      }), /*#__PURE__*/jsx("path", {
        d: "M16.5.5H4a3 3 0 00-3 3V23c0 .13.05.26.14.36.1.09.23.14.36.14H4M4 4.5h7M4 7.5h7M4 10.5h3.5"
      }), /*#__PURE__*/jsx("path", {
        d: "M14 13V3A2.5 2.5 0 0116.5.5c1.38 0 2.5 1.12 2.5 3h-5"
      })]
    }))
  });
}
ContractHandshakeIcon.displayName = 'ContractHandshakeIcon';

var _path$r;
const copyIconClassName = 'bui-copy-icon';

/**
 * A component that displays a copy icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { CopyIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <CopyIcon/>
 * }
 * ```
 */
function CopyIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(copyIconClassName, className),
    label: label,
    children: _path$r || (_path$r = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "m11.986 8.132-3.511 3.51a.5.5 0 0 0 0 .707l3.51 3.51h.001l1.06 1.06 1.086 1.087a.498.498 0 0 0 .708 0l5.657-5.657a.5.5 0 0 0 0-.707L14.84 5.985a.498.498 0 0 0-.708 0L13.046 7.07l-1.06 1.061Zm1.085-3.208L11.985 6.01 10.9 4.924a2 2 0 0 0-2.829 0l-5.657 5.657a2 2 0 0 0 0 2.829l5.657 5.656a2 2 0 0 0 2.829 0l1.085-1.085 1.086 1.085a2 2 0 0 0 2.829 0l5.656-5.656a2 2 0 0 0 0-2.829L15.9 4.924a2 2 0 0 0-2.829 0ZM9.84 18.006l1.086-1.086-3.51-3.51a2 2 0 0 1 0-2.829l3.51-3.51-1.086-1.086a.5.5 0 0 0-.707 0l-5.657 5.657a.5.5 0 0 0 0 .707l5.657 5.657a.5.5 0 0 0 .707 0Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
CopyIcon.displayName = 'CopyIcon';

var _path$q;
const creditCardIconClassName = 'bui-credit-card-icon';

/**
 * A component that displays a credit card icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { CreditCardIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <CreditCardIcon/>
 * }
 * ```
 */
function CreditCardIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(creditCardIconClassName, className),
    label: label,
    children: _path$q || (_path$q = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M20.92 19.2H3.08c-.37 0-.68-.33-.68-.76V9.6h19.2v8.84c0 .43-.31.76-.68.76zm.68-10.8H2.4V6.16c0-.43.31-.76.68-.76h17.84c.37 0 .68.33.68.76V8.4zM1.2 9V6.16c0-1.07.83-1.96 1.88-1.96h17.84c1.05 0 1.88.89 1.88 1.96v12.28c0 1.07-.83 1.96-1.88 1.96H3.08a1.92 1.92 0 01-1.88-1.96V9zM6 13.8a.6.6 0 010-1.2h7.2a.6.6 0 110 1.2H6zm-.6 1.8c0 .33.27.6.6.6h5.4a.6.6 0 000-1.2H6a.6.6 0 00-.6.6z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
CreditCardIcon.displayName = 'CreditCardIcon';

var _g$g;
const dataDownloadIconClassName = 'bui-data-download-icon';

/**
 * A component that displays a download data icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { DataDownloadIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <DataDownloadIcon/>
 * }
 * ```
 */
function DataDownloadIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(dataDownloadIconClassName, className),
    label: label,
    children: _g$g || (_g$g = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [/*#__PURE__*/jsx("path", {
        d: "M12 10c-5 0-9-1-9-3s4-4 9-4 9 2 9 4c0 1-1 2-3 2m3 1V7"
      }), /*#__PURE__*/jsx("path", {
        d: "M3 7v4c0 2 3 3 6 4"
      }), /*#__PURE__*/jsx("path", {
        d: "M3 11v5c0 2 3 3 7 3m1-3a5 5 0 1 0 10 0 5 5 0 0 0-10 0Zm5-2v4m0 0-2-1m2 1 2-1"
      })]
    }))
  });
}
DataDownloadIcon.displayName = 'DataDownloadIcon';

var _g$f;
const databaseIconClassName = 'bui-database-icon';

/**
 * A component that displays a database icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { DatabaseIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <DatabaseIcon/>
 * }
 * ```
 */
function DatabaseIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(databaseIconClassName, className),
    label: label,
    children: _g$f || (_g$f = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.25",
      children: [/*#__PURE__*/jsx("path", {
        d: "M17.7 10V4.9M11.2 8.2c-.5 0-1 .1-1.6.1-4.5 0-8.1-1.5-8.1-3.4s3.6-3.4 8.1-3.4 8.1 1.5 8.1 3.4c0 1-1.1 1.9-2.7 2.5"
      }), /*#__PURE__*/jsx("path", {
        d: "M7.8 17.2c-3.6-.4-6.2-1.7-6.2-3.3v-9 4.5c0 1.5 2.3 2.7 5.4 3.2"
      }), /*#__PURE__*/jsx("path", {
        d: "M13.9 9.8l1.1 3.1h2.2c.2 0 .4.1.5.3.1.2 0 .5-.1.6l-1.9 1.3 1 2.5c.1.2 0 .5-.1.7-.2.2-.4.2-.7.1l-2.5-1.5-2.5 1.5c-.2.1-.5.1-.7-.1-.2-.2-.2-.4-.1-.7l1-2.5-1.9-1.3c-.2-.2-.2-.4-.1-.6.1-.2.3-.4.5-.3h2.2l1.1-3.1c.1-.2.3-.3.5-.3s.4.1.5.3h0z"
      })]
    }))
  });
}
DatabaseIcon.displayName = 'DatabaseIcon';

var _g$e;
const documentWithBookmarkIconClassName = 'bui-document-with-bookmark-icon';

/**
 * A component that displays a document with a bookmark icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { DocumentWithBookmarkIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <DocumentWithBookmarkIcon/>
 * }
 * ```
 */
function DocumentWithBookmarkIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(documentWithBookmarkIconClassName, className),
    label: label,
    children: _g$e || (_g$e = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [/*#__PURE__*/jsx("path", {
        d: "M6.375 3.874h-2.5a1.25 1.25 0 0 0-1.25 1.25v15a1.25 1.25 0 0 0 1.25 1.25h16.25a1.25 1.25 0 0 0 1.25-1.25v-15a1.25 1.25 0 0 0-1.25-1.25h-8.75"
      }), /*#__PURE__*/jsx("path", {
        d: "M11.375 11.374 8.875 9.5l-2.5 1.875V3.25A.625.625 0 0 1 7 2.624h3.75a.625.625 0 0 1 .625.625v8.125ZM6.375 17.624h8.75M6.375 13.874h11.25M17.625 10.124h-3.75"
      })]
    }))
  });
}
DocumentWithBookmarkIcon.displayName = 'DocumentWithBookmarkIcon';

var _path$p;
const downloadIconClassName = 'bui-download-icon';

/**
 * A component that displays an upload icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { DownloadIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <DownloadIcon/>
 * }
 * ```
 */
function DownloadIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(downloadIconClassName, className),
    label: label,
    children: _path$p || (_path$p = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M19 9.5h-4v-6H9v6H5l7 7 7-7zm-8 2v-6h2v6h1.2L12 13.7l-2.2-2.2H11zm8 9v-2H5v2h14z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
DownloadIcon.displayName = 'DownloadIcon';

var _path$o;
const downChevronIconClassName = 'bui-down-chevron-icon';

/**
 * A component that displays a down chevron icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { DownChevronIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <DownChevronIcon/>
 * }
 * ```
 */
function DownChevronIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(downChevronIconClassName, className),
    label: label,
    children: _path$o || (_path$o = /*#__PURE__*/jsx("path", {
      d: "M18 9.7l-1.41-1.4L12 12.87 7.41 8.3 6 9.7l6 6 6-6z",
      fill: "currentColor"
    }))
  });
}
DownChevronIcon.displayName = 'DownChevronIcon';

var _path$n;
const errorIconClassName = 'bui-error-icon';

/**
 * A component that displays an error icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ErrorIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ErrorIcon/>
 * }
 * ```
 */
function ErrorIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(errorIconClassName, className),
    label: label,
    children: _path$n || (_path$n = /*#__PURE__*/jsx("path", {
      d: "M12 21.75a9.75 9.75 0 100-19.5 9.75 9.75 0 000 19.5zm2.92-13.71c.3-.3.76-.3 1.06 0 .28.28.28.76 0 1.05L13.06 12l2.92 2.92a.75.75 0 11-1.06 1.06L12 13.06l-2.91 2.92a.75.75 0 11-1.06-1.06L10.95 12 8.03 9.09a.75.75 0 111.06-1.06L12 10.95zm0 0",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
ErrorIcon.displayName = 'ErrorIcon';

var _g$d;
const exchangeIconClassName = 'bui-exchange-icon';

/**
 * A component that displays an exchange icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ExchangeIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ExchangeIcon/>
 * }
 * ```
 */
function ExchangeIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(exchangeIconClassName, className),
    label: label,
    children: _g$d || (_g$d = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.3",
      children: [/*#__PURE__*/jsx("path", {
        d: "M13.25 14.92H6.58M17.42 9.08h-6.67M14.92 11.58l2.5-2.5-2.5-2.5M9.08 17.42l-2.5-2.5 2.5-2.5"
      }), /*#__PURE__*/jsx("path", {
        d: "M12 21.58a9.58 9.58 0 100-19.16 9.58 9.58 0 000 19.16z"
      })]
    }))
  });
}
ExchangeIcon.displayName = 'ExchangeIcon';

var _g$c;
const fileIconClassName = 'bui-file-icon';

/**
 * A component that displays a generic file icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { FileIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <FileIcon/>
 * }
 * ```
 */
function FileIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(fileIconClassName, className),
    label: label,
    children: _g$c || (_g$c = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5",
      children: [/*#__PURE__*/jsx("path", {
        d: "M20.25 8.25h-6a1.5 1.5 0 01-1.5-1.5v-6"
      }), /*#__PURE__*/jsx("path", {
        d: "M18.64 23.25a1.61 1.61 0 001.61-1.61V8.12a1.5 1.5 0 00-.44-1.06l-5.87-5.87a1.5 1.5 0 00-1.06-.44H2.25a1.5 1.5 0 00-1.5 1.5v19.5a1.5 1.5 0 001.5 1.5h16.39z"
      })]
    }))
  });
}
FileIcon.displayName = 'FileIcon';

var _path$m;
const flagIconClassName = 'bui-flag-icon';

/**
 * A component that displays a generic flag icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { FlagIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <FlagIcon/>
 * }
 * ```
 */
function FlagIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(flagIconClassName, className),
    label: label,
    children: _path$m || (_path$m = /*#__PURE__*/jsx("path", {
      d: "M2.625 21.375V2.625M3 16.099l2.523-.6a9.845 9.845 0 0 1 3.232-.217 9.242 9.242 0 0 1 3.06.835c.909.433 1.925.715 2.982.828a9.866 9.866 0 0 0 3.16-.174l2.39-.528a.942.942 0 0 0 .471-.25.602.602 0 0 0 .182-.42V5.787a.572.572 0 0 0-.095-.31.776.776 0 0 0-.263-.246 1.047 1.047 0 0 0-.377-.129 1.156 1.156 0 0 0-.412.015l-1.895.42a9.867 9.867 0 0 1-3.16.173 9.232 9.232 0 0 1-2.984-.828 9.243 9.243 0 0 0-3.059-.834 9.846 9.846 0 0 0-3.232.215L3 4.863",
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }))
  });
}
FlagIcon.displayName = 'FlagIcon';

var _path$l;
const generalIconClassName = 'bui-general-icon';

/**
 * A component that displays a general purpose icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { GeneralIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <GeneralIcon/>
 * }
 * ```
 */
function GeneralIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(generalIconClassName, className),
    label: label,
    children: _path$l || (_path$l = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M4.54 2.4h4.72c.58 0 .89.06 1.2.23.3.16.55.4.7.7.18.32.24.63.24 1.2v4.73c0 .58-.06.89-.23 1.2-.16.3-.4.55-.7.7-.32.18-.63.24-1.2.24H4.53c-.58 0-.89-.06-1.2-.23-.3-.16-.55-.4-.7-.7a2.32 2.32 0 01-.24-1.2V4.53c0-.58.06-.89.23-1.2.16-.3.4-.55.7-.7.32-.18.63-.24 1.2-.24zm0 1.2c-.4 0-.52.02-.64.09a.5.5 0 00-.21.2c-.07.13-.09.25-.09.65v4.72c0 .4.02.52.09.64.05.1.12.16.2.21.13.07.25.09.65.09h4.72c.4 0 .52-.02.64-.09a.5.5 0 00.21-.2c.07-.13.09-.25.09-.65V4.54c0-.4-.02-.52-.09-.64a.5.5 0 00-.2-.21c-.13-.07-.25-.09-.65-.09H4.54zm10.2-1.2h4.72c.58 0 .89.06 1.2.23.3.16.55.4.7.7.18.32.24.63.24 1.2v4.73c0 .58-.06.89-.23 1.2-.16.3-.4.55-.7.7-.32.18-.63.24-1.2.24h-4.73c-.58 0-.89-.06-1.2-.23-.3-.16-.55-.4-.7-.7a2.32 2.32 0 01-.24-1.2V4.53c0-.58.06-.89.23-1.2.16-.3.4-.55.7-.7.32-.18.63-.24 1.2-.24zm0 1.2c-.4 0-.52.02-.64.09a.5.5 0 00-.21.2c-.07.13-.09.25-.09.65v4.72c0 .4.02.52.09.64.05.1.12.16.2.21.13.07.25.09.65.09h4.72c.4 0 .52-.02.64-.09a.5.5 0 00.21-.2c.07-.13.09-.25.09-.65V4.54c0-.4-.02-.52-.09-.64a.5.5 0 00-.2-.21c-.13-.07-.25-.09-.65-.09h-4.72zm-5.48 9H4.54c-.58 0-.89.06-1.2.23-.3.16-.55.4-.7.7-.18.32-.24.63-.24 1.2v4.73c0 .58.06.89.23 1.2.16.3.4.55.7.7.32.18.63.24 1.2.24h4.73c.58 0 .89-.06 1.2-.23.3-.16.55-.4.7-.7.18-.32.24-.63.24-1.2v-4.73c0-.58-.06-.89-.23-1.2-.16-.3-.4-.55-.7-.7a2.32 2.32 0 00-1.2-.24zM3.9 13.89c.12-.07.24-.09.64-.09h4.72c.4 0 .52.02.64.09.1.05.16.12.21.2.07.13.09.25.09.65v4.72c0 .4-.02.52-.09.64a.5.5 0 01-.2.21c-.13.07-.25.09-.65.09H4.54c-.4 0-.52-.02-.64-.09a.5.5 0 01-.21-.2c-.07-.13-.09-.25-.09-.65v-4.72c0-.4.02-.52.09-.64a.5.5 0 01.2-.21zm10.84-1.29h4.72c.58 0 .89.06 1.2.23.3.16.55.4.7.7.18.32.24.63.24 1.2v4.73c0 .58-.06.89-.23 1.2-.16.3-.4.55-.7.7-.32.18-.63.24-1.2.24h-4.73c-.58 0-.89-.06-1.2-.23-.3-.16-.55-.4-.7-.7a2.32 2.32 0 01-.24-1.2v-4.73c0-.58.06-.89.23-1.2.16-.3.4-.55.7-.7.32-.18.63-.24 1.2-.24zm0 1.2c-.4 0-.52.02-.64.09a.5.5 0 00-.21.2c-.07.13-.09.25-.09.65v4.72c0 .4.02.52.09.64.05.1.12.16.2.21.13.07.25.09.65.09h4.72c.4 0 .52-.02.64-.09a.5.5 0 00.21-.2c.07-.13.09-.25.09-.65v-4.72c0-.4-.02-.52-.09-.64a.5.5 0 00-.2-.21c-.13-.07-.25-.09-.65-.09h-4.72z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
GeneralIcon.displayName = 'GeneralIcon';

var _path$k, _path2$1, _path3, _path4;
const googleIconClassName = 'bui-google-icon';

/**
 * A component that displays a Google logo icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { GoogleIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <GoogleIcon/>
 * }
 * ```
 */
function GoogleIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsxs(Icon, {
    className: classnames(googleIconClassName, className),
    label: label,
    children: [_path$k || (_path$k = /*#__PURE__*/jsx("path", {
      d: "M22.5 12.225c0-.658-.06-1.283-.162-1.892h-9.62v3.759h5.508c-.247 1.233-.97 2.275-2.043 2.983v2.5h3.286c1.924-1.742 3.031-4.308 3.031-7.35Z",
      fill: "#4285F4"
    })), _path2$1 || (_path2$1 = /*#__PURE__*/jsx("path", {
      d: "M12.717 22c2.758 0 5.066-.9 6.752-2.425l-3.287-2.5c-.92.6-2.086.967-3.465.967-2.665 0-4.921-1.759-5.73-4.134H3.598v2.575C5.275 19.75 8.724 22 12.717 22Z",
      fill: "#34A853"
    })), _path3 || (_path3 = /*#__PURE__*/jsx("path", {
      d: "M6.987 13.908A5.69 5.69 0 0 1 6.663 12c0-.667.12-1.308.324-1.908V7.517H3.598A9.714 9.714 0 0 0 2.5 12c0 1.617.4 3.133 1.098 4.483l3.389-2.575Z",
      fill: "#FBBC05"
    })), _path4 || (_path4 = /*#__PURE__*/jsx("path", {
      d: "M12.717 5.958c1.507 0 2.852.509 3.917 1.5l2.911-2.85C17.783 2.992 15.475 2 12.717 2 8.724 2 5.275 4.25 3.598 7.517l3.389 2.575c.809-2.375 3.065-4.134 5.73-4.134Z",
      fill: "#EA4335"
    }))]
  });
}
GoogleIcon.displayName = 'GoogleIcon';

var _path$j;
const heartbeatIconClassName = 'bui-heartbeat-icon';

/**
 * A component that displays a heartbeat icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { HeartbeatIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <HeartbeatIcon/>
 * }
 * ```
 */
function HeartbeatIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(heartbeatIconClassName, className),
    label: label,
    children: _path$j || (_path$j = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M21.6 8.17a9.91 9.91 0 01-1.06 4.2.6.6 0 01-1.07-.55c.58-1.14.9-2.4.93-3.67 0-5.42-6.97-5.8-7.8-.38-.11.68-1.09.68-1.2 0-.84-5.51-7.8-4.94-7.8.85.01 1.11.25 2.21.68 3.24a.6.6 0 11-1.1.47 9.78 9.78 0 01-.78-3.7c0-6.48 7.38-7.8 9.6-3.04a5.05 5.05 0 019.6 2.58zM8.2 13.89a1 1 0 01-.8.4H2.8a.6.6 0 110-1.2h4.5l.68-.9a1 1 0 011.63.04l.72 1.07 1.07-2.15a1 1 0 011.86.17l.9 3.13.4-.8a1 1 0 01.89-.55h5.75a.6.6 0 110 1.2h-5.63L15 15.45a1 1 0 01-1.86-.18l-.9-3.13-1 2a1 1 0 01-1.72.11l-.75-1.12-.57.76zm10.76.93a.66.66 0 00-.9.1c-1.18 1.44-2.96 2.78-4.6 4L12 20.05c-2.12-1.45-4.46-3.16-6.07-5.12a.66.66 0 00-.88-.1.58.58 0 00-.12.84 29.8 29.8 0 006.35 5.46c.39.26 1.07.25 1.44-.04l1.24-.94c1.8-1.37 3.78-2.87 5.1-4.48a.58.58 0 00-.1-.84z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
HeartbeatIcon.displayName = 'HeartbeatIcon';

var _g$b;
const hideIconClassName = 'bui-hide-icon';

/**
 * A component that displays an invisible/hide icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ShowIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ShowIcon/>
 * }
 * ```
 */
function HideIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(hideIconClassName, className),
    label: label,
    children: _g$b || (_g$b = /*#__PURE__*/jsxs("g", {
      clipRule: "evenodd",
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5",
      children: [/*#__PURE__*/jsx("path", {
        d: "m2.78 21.2 18.75-18M8.96 19.25c.99.3 2.01.46 3.04.45 4.1.07 8.26-2.81 10.82-5.64.57-.63.57-1.59 0-2.22a20.57 20.57 0 0 0-3.07-2.76M14.41 6.48c-.79-.2-1.6-.29-2.41-.28-4.03-.07-8.2 2.75-10.82 5.64-.57.63-.57 1.59 0 2.22.8.87 1.67 1.68 2.6 2.4"
      }), /*#__PURE__*/jsx("path", {
        d: "M8.25 12.95A3.75 3.75 0 0 1 12 9.2M15.75 12.95v0A3.75 3.75 0 0 1 12 16.7"
      })]
    }))
  });
}
HideIcon.displayName = 'HideIcon';

var _path$i;
const hideTableIconClassName = 'bui-hide-table-icon';

/**
 * A component that displays a hide table icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { HideTableIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <HideTableIcon/>
 * }
 * ```
 */
function HideTableIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(hideTableIconClassName, className),
    label: label,
    children: _path$i || (_path$i = /*#__PURE__*/jsx("path", {
      d: "M11.57 21.58l10-7.5M9.9 17.42H4.08a1.67 1.67 0 01-1.66-1.67V4.08a1.67 1.67 0 011.66-1.66h11.67a1.67 1.67 0 011.67 1.66v8.34M2.4 7.42h15M2.4 12.42h15M12.4 2.42v10.83M7.4 2.42v15M11.88 19.15a1.9 1.9 0 01-.13-1.85c.73-1.62 3.45-2.65 6.09-2.32M21.19 16.61a1.92 1.92 0 01.22 2c-.73 1.63-3.5 2.67-6.16 2.3",
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.1"
    }))
  });
}
HideTableIcon.displayName = 'HideTableIcon';

var _path$h;
const holidayIconClassName = 'bui-holiday-icon';

/**
 * A component that displays a holiday icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { HolidayIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <HolidayIcon/>
 * }
 * ```
 */
function HolidayIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(holidayIconClassName, className),
    label: label,
    children: _path$h || (_path$h = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M19.7 7.08a7.17 7.17 0 0 0-7.93-2.3l-.32-.94a.5.5 0 0 0-.95.32l.34 1a7.17 7.17 0 0 0-3.9 6.36 1.17 1.17 0 0 0 1.54 1.1l4.89-1.7 1.4 3.7a8.5 8.5 0 0 0-3.03 1.1l-.08.04-.4.22a.5.5 0 1 0 .47.88l.41-.22.07-.04.54-.27c1.03-.5 2-.78 3.08-.8 1.25 0 2.5.32 3.77.99a.5.5 0 0 0 .47-.89 8.8 8.8 0 0 0-4.25-1.1h-.02l-1.49-3.93 4.86-1.7a1.17 1.17 0 0 0 .53-1.82ZM13.56 9.8l5.28-1.84a.17.17 0 0 0 .08-.26 6.17 6.17 0 0 0-10.99 3.83.17.17 0 0 0 .23.16l5.27-1.84a.5.5 0 0 1 .13-.05ZM3.6 19.17a.58.58 0 0 1 .1-.78.5.5 0 0 1 .72.1l.6.83a.17.17 0 0 0 .26 0l.67-.85a1.16 1.16 0 0 1 1.7-.17c.06.05.11.1.16.17l.67.85a.17.17 0 0 0 .27 0l.67-.85a1.16 1.16 0 0 1 1.7-.17c.06.05.11.1.16.17l.67.85a.17.17 0 0 0 .27 0l.66-.85a1.16 1.16 0 0 1 1.71-.17c.06.05.11.1.16.17l.67.85a.17.17 0 0 0 .26 0l.67-.85a1.16 1.16 0 0 1 1.71-.17c.06.05.11.1.16.17l.67.85a.17.17 0 0 0 .26 0l.42-.53a.5.5 0 0 1 .73-.07c.22.2.25.55.07.78l-.42.53-.15.17a1.16 1.16 0 0 1-1.71-.17l-.67-.85a.17.17 0 0 0-.27 0l-.67.85-.15.17a1.16 1.16 0 0 1-1.71-.17l-.67-.85a.17.17 0 0 0-.27 0l-.67.85-.15.17a1.16 1.16 0 0 1-1.71-.17l-.67-.85a.17.17 0 0 0-.27 0l-.66.85-.16.17a1.16 1.16 0 0 1-1.7-.17l-.68-.85a.17.17 0 0 0-.27 0l-.66.85-.16.17c-.51.46-1.28.38-1.72-.19l-.6-.84Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
HolidayIcon.displayName = 'HolidayIcon';

var _path$g, _path2;
const houseIconClassName = 'bui-house-icon';

/**
 * A component that displays a house icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { HouseIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <HouseIcon/>
 * }
 * ```
 */
function HouseIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsxs(Icon, {
    className: classnames(houseIconClassName, className),
    label: label,
    children: [_path$g || (_path$g = /*#__PURE__*/jsx("path", {
      d: "M13.88 18.52a.64.64 0 01-.63-.65v-3.88c0-.36.28-.65.63-.65h3.75c.34 0 .62.3.62.65v3.88c0 .36-.28.65-.62.65h-3.75zm3.12-1.3v-2.58h-2.5v2.58H17z",
      fill: "currentColor"
    })), _path2 || (_path2 = /*#__PURE__*/jsx("path", {
      d: "M2.63 21.1a.64.64 0 01-.63-.65c0-.35.28-.64.63-.64h.62v-8.72l-.25.19a.61.61 0 01-.88-.14.66.66 0 01.14-.9l8.63-6.56a1.83 1.83 0 012.22 0l1.39 1.06v-1.1c0-.35.28-.64.63-.64h3.75c.34 0 .62.29.62.65v4.88l2.24 1.7c.28.22.34.62.14.9a.62.62 0 01-.5.27.6.6 0 01-.38-.12l-.25-.2v8.73h.63c.34 0 .62.29.62.64 0 .36-.28.65-.63.65H2.64zm16.87-1.3v-9.66l-7.13-5.41a.6.6 0 00-.74 0l-7.13 5.4v9.68H7v-4.53c0-1.07.84-1.94 1.88-1.94h1.25c1.03 0 1.87.87 1.87 1.94v4.53h7.5zm-8.75 0v-4.52a.64.64 0 00-.62-.64H8.88a.64.64 0 00-.63.64v4.53h2.5zm7.5-12.21v-3.3h-2.5v1.4l2.5 1.9z",
      fill: "currentColor"
    }))]
  });
}
HouseIcon.displayName = 'HouseIcon';

var _path$f;
const infoIconClassName = 'bui-info-icon';

/**
 * A component that displays an information icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { InfoIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <InfoIcon/>
 * }
 * ```
 */
function InfoIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(infoIconClassName, className),
    label: label,
    children: _path$f || (_path$f = /*#__PURE__*/jsx("path", {
      d: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm.2 4.17a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Zm1.88 11.25h-3.33a.83.83 0 0 1 0-1.67h.63a.2.2 0 0 0 .2-.2v-3.76a.2.2 0 0 0-.2-.2h-.63a.83.83 0 0 1 0-1.67h.83a1.67 1.67 0 0 1 1.67 1.66v3.96a.2.2 0 0 0 .2.21h.63a.83.83 0 1 1 0 1.67Z",
      fill: "currentColor"
    }))
  });
}
InfoIcon.displayName = 'InfoIcon';

var _path$e;
const leftChevronIconClassName = 'bui-left-chevron-icon';

/**
 * A component that displays a left chevron icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { LeftChevronIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <LeftChevronIcon/>
 * }
 * ```
 */
function LeftChevronIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(leftChevronIconClassName, className),
    label: label,
    children: _path$e || (_path$e = /*#__PURE__*/jsx("path", {
      d: "M14.3 18l1.4-1.41L11.13 12l4.57-4.59L14.3 6l-6 6 6 6z",
      fill: "currentColor"
    }))
  });
}
LeftChevronIcon.displayName = 'LeftChevronIcon';

var _g$a;
const letterIconClassName = 'bui-letter-icon';

/**
 * A component that displays a letter icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { LetterIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <LetterIcon/>
 * }
 * ```
 */
function LetterIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(letterIconClassName, className),
    label: label,
    children: _g$a || (_g$a = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [/*#__PURE__*/jsx("path", {
        d: "M20.125 5.75H3.875C3.18464 5.75 2.625 6.30964 2.625 7V17C2.625 17.6904 3.18464 18.25 3.875 18.25H20.125C20.8154 18.25 21.375 17.6904 21.375 17V7C21.375 6.30964 20.8154 5.75 20.125 5.75Z"
      }), /*#__PURE__*/jsx("path", {
        d: "M15.0725 10.3125L18.25 13.25"
      }), /*#__PURE__*/jsx("path", {
        d: "M8.9275 10.3125L5.75 13.25"
      }), /*#__PURE__*/jsx("path", {
        d: "M21.0667 6.17822L13.1392 11.6449C12.8043 11.8761 12.407 11.9999 12 11.9999C11.5931 11.9999 11.1957 11.8761 10.8608 11.6449L2.93335 6.17822"
      })]
    }))
  });
}
LetterIcon.displayName = 'LetterIcon';

var _path$d;
const logoutIconClassName = 'bui-logout-icon';

/**
 * A component that displays a logout icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { LogoutIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <LogoutIcon/>
 * }
 * ```
 */
function LogoutIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(logoutIconClassName, className),
    label: label,
    children: _path$d || (_path$d = /*#__PURE__*/jsx("path", {
      d: "M9 12h10.5M17 14.5l2.5-2.5L17 9.5M14.5 15v3a.96.96 0 01-.9 1H5.4a.96.96 0 01-.9-1V6a.96.96 0 01.9-1h8.2c.52.03.92.47.9 1v3",
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: 1.5
    }))
  });
}
LogoutIcon.displayName = 'LogoutIcon';

var _g$9;
const padlockBadgeIconClassName = 'bui-padlock-badge-icon';

/**
 * A component that displays a padlock badge icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { PadlockBadgeIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <PadlockBadgeIcon/>
 * }
 * ```
 */
function PadlockBadgeIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(padlockBadgeIconClassName, className),
    label: label,
    children: _g$9 || (_g$9 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      children: [/*#__PURE__*/jsx("path", {
        d: "M3.875 5.27v6.344a9.922 9.922 0 0 0 6.36 9.261l.867.333a2.5 2.5 0 0 0 1.796 0l.867-.333a9.923 9.923 0 0 0 6.36-9.26V5.268a1.24 1.24 0 0 0-.723-1.135A18.083 18.083 0 0 0 12 2.625a18.083 18.083 0 0 0-7.402 1.51 1.24 1.24 0 0 0-.723 1.134Z"
      }), /*#__PURE__*/jsx("path", {
        d: "M14.5 10.125h-5c-.69 0-1.25.56-1.25 1.25v3.75c0 .69.56 1.25 1.25 1.25h5c.69 0 1.25-.56 1.25-1.25v-3.75c0-.69-.56-1.25-1.25-1.25ZM12 6.375a2.5 2.5 0 0 0-2.5 2.5v1.25h5v-1.25a2.5 2.5 0 0 0-2.5-2.5Z"
      }), /*#__PURE__*/jsx("path", {
        d: "M12 13.587a.312.312 0 1 1 0-.625M12 13.587a.312.312 0 1 0 0-.625"
      })]
    }))
  });
}
PadlockBadgeIcon.displayName = 'PadlockBadgeIcon';

var _g$8;
const pdfFileIconClassName = 'bui-pdf-file-icon';

/**
 * A component that displays a PDF file icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { PdfFileIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <PdfFileIcon/>
 * }
 * ```
 */
function PdfFileIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(pdfFileIconClassName, className),
    label: label,
    children: _g$8 || (_g$8 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5",
      children: [/*#__PURE__*/jsx("path", {
        d: "M5.25 23.25h-3a1.5 1.5 0 01-1.5-1.5V2.25a1.5 1.5 0 011.5-1.5h10.63a1.5 1.5 0 011.06.44l5.87 5.87a1.5 1.5 0 01.44 1.06v4.63"
      }), /*#__PURE__*/jsx("path", {
        d: "M20.25 8.25h-6a1.5 1.5 0 01-1.5-1.5v-6M8.25 23.25v-7.5M8.25 15.75H9a2.25 2.25 0 010 4.5h-.75M14.25 23.25a3 3 0 003-3v-1.5a3 3 0 00-3-3zM20.25 23.25v-6a1.5 1.5 0 011.5-1.5h1.5M20.25 20.25h2.25"
      })]
    }))
  });
}
PdfFileIcon.displayName = 'PdfFileIcon';

var _path$c;
const pencilIconClassName = 'bui-pencil-icon';

/**
 * A component that displays a pencil icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { PencilIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <PencilIcon/>
 * }
 * ```
 */
function PencilIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(pencilIconClassName, className),
    label: label,
    children: _path$c || (_path$c = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M17.66 3a1 1 0 00-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83a1 1 0 000-1.41l-2.34-2.34a.98.98 0 00-.71-.29zm-3.6 6.02l.92.92L5.92 19H5v-.92l9.06-9.06zM3 17.25L14.06 6.19l3.75 3.75L6.75 21H3v-3.75z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
PencilIcon.displayName = 'PencilIcon';

var _path$b;
const piggyBankIconClassName = 'bui-piggy-bank-icon';

/**
 * A component that displays a piggy bank icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { PiggyBankIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <PiggyBankIcon/>
 * }
 * ```
 */
function PiggyBankIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(piggyBankIconClassName, className),
    label: label,
    children: _path$b || (_path$b = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M11.78 5.55a2.18 2.18 0 100-4.35 2.18 2.18 0 000 4.35zm0-1.2a.97.97 0 110-1.95.97.97 0 010 1.95zm-1.91 6.57a.6.6 0 01-.54-1.07 5.93 5.93 0 015.34 0 .6.6 0 01-.54 1.07 4.73 4.73 0 00-4.26 0zm10.53 1.33a.6.6 0 011.2 0 2.4 2.4 0 01-1.82 2.33 7.1 7.1 0 01-2.38 4.74v2.06a1.2 1.2 0 01-1.2 1.2H15a1.2 1.2 0 01-1.2-1.2v-.21a8.08 8.08 0 01-3.6-.01v.22a1.2 1.2 0 01-1.2 1.2H7.8a1.2 1.2 0 01-1.2-1.2V19.3c-.52-.47-.98-1.01-1.35-1.6H3.6a.6.6 0 01-.6-.6v-4.9c0-.32.27-.6.6-.6h1.05c.28-.76.69-1.47 1.2-2.1l-.44-2.07a.6.6 0 01.37-.69 4.3 4.3 0 012.43-.2c.48.11.9.31 1.27.62 4.69-1.38 9.8 1.35 10.28 6.14.38-.2.64-.6.64-1.06zM8.1 14.04a.9.9 0 000-1.81.9.9 0 000 1.81zm-1.4-6.3l.39 1.82a.6.6 0 01-.14.52 5.99 5.99 0 00-1.3 2.3.6.6 0 01-.57.44H4.2v3.68h1.4a.6.6 0 01.52.3c.37.68.87 1.28 1.46 1.77a.6.6 0 01.22.46v2.35H9v-1.01c0-.42.4-.7.8-.57 1.43.48 2.97.49 4.4 0a.6.6 0 01.8.57v1.01h1.2v-2.34a.6.6 0 01.22-.46 5.9 5.9 0 002.18-4.53c0-4.46-4.78-7.1-9.09-5.63a.6.6 0 01-.64-.17 1.7 1.7 0 00-.92-.52c-.39-.1-.82-.08-1.25.01z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
PiggyBankIcon.displayName = 'PiggyBankIcon';

var _path$a;
const plusIconClassName = 'bui-plus-icon';

/**
 * A component that displays a plus icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { PlusIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <PlusIcon/>
 * }
 * ```
 */
function PlusIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(plusIconClassName, className),
    label: label,
    children: _path$a || (_path$a = /*#__PURE__*/jsx("path", {
      d: "M12 8v8m-4-4h8M2 12a10 10 0 1 0 20 0 10 10 0 0 0-20 0v0Z",
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5"
    }))
  });
}
PlusIcon.displayName = 'PlusIcon';

var _g$7;
const priceTagIconClassName = 'bui-price-tag-icon';

/**
 * A component that displays a price tag icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { PriceTagIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <PriceTagIcon/>
 * }
 * ```
 */
function PriceTagIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(priceTagIconClassName, className),
    label: label,
    children: _g$7 || (_g$7 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.3",
      children: [/*#__PURE__*/jsx("path", {
        d: "M16.29 9.57l1.6-1.63c.36-.42.58-.96.61-1.52V2.88a.88.88 0 00-.88-.88h-3.77c-.56.04-1.1.26-1.51.63l-9.58 9.58a.88.88 0 000 1.25l4.28 4.28a.88.88 0 001.24 0l.93-.95"
      }), /*#__PURE__*/jsx("path", {
        d: "M15 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17.7 14.5h-1.63c-.25 0-.49.09-.68.26a1.16 1.16 0 00-.26 1.4c.12.22.31.4.54.5l1.65.68c.24.1.43.28.55.5a1.16 1.16 0 01-.26 1.4c-.2.17-.44.26-.69.26H15.3M17 20.5v-1M17 14.5v-1"
      }), /*#__PURE__*/jsx("path", {
        d: "M16.5 22a5 5 0 100-10 5 5 0 000 10z"
      })]
    }))
  });
}
PriceTagIcon.displayName = 'PriceTagIcon';

var _g$6;
const questionmarkCircleIconClassName = 'bui-questionmark-circle-icon';

/**
 * A component that displays a QuestionmarkCircle icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { QuestionmarkCircleIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <QuestionmarkCircleIcon/>
 * }
 * ```
 */
function QuestionmarkCircleIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(questionmarkCircleIconClassName, className),
    label: label,
    children: _g$6 || (_g$6 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5",
      children: [/*#__PURE__*/jsx("path", {
        d: "M8.5 8.5a2.5 2.5 0 1 1 3.33 2.36 1.25 1.25 0 0 0-.83 1.18v.84"
      }), /*#__PURE__*/jsx("path", {
        d: "M1.63 11a9.37 9.37 0 1 0 18.74 0 9.37 9.37 0 0 0-18.75 0Z"
      }), /*#__PURE__*/jsx("path", {
        d: "M11 15.38a.31.31 0 1 1 0 .63.31.31 0 0 1 0-.63"
      })]
    }))
  });
}
QuestionmarkCircleIcon.displayName = 'QuestionmarkCircleIcon';

var _path$9;
const rightChevronIconClassName = 'bui-right-chevron-icon';

/**
 * A component that displays a right chevron icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { RightChevronIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <RightChevronIcon/>
 * }
 * ```
 */
function RightChevronIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(rightChevronIconClassName, className),
    label: label,
    children: _path$9 || (_path$9 = /*#__PURE__*/jsx("path", {
      d: "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z",
      fill: "currentColor"
    }))
  });
}
RightChevronIcon.displayName = 'RightChevronIcon';

var _path$8;
const searchIconClassName = 'bui-search-icon';

/**
 * A component that displays a search icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { SearchIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <SearchIcon/>
 * }
 * ```
 */
function SearchIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(searchIconClassName, className),
    label: label,
    children: _path$8 || (_path$8 = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M15.305 14.255h.79l4.99 5-1.49 1.49-5-4.99v-.79l-.27-.28a6.471 6.471 0 01-4.23 1.57 6.5 6.5 0 116.5-6.5c0 1.61-.59 3.09-1.57 4.23l.28.27zm-9.71-4.5c0 2.49 2.01 4.5 4.5 4.5s4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5-4.5 2.01-4.5 4.5z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
SearchIcon.displayName = 'SearchIcon';

var _g$5;
const secureSyncIconClassName = 'bui-secure-sync-icon';

/**
 * A component that displays a secure sync icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { SecureSyncIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <SecureSyncIcon/>
 * }
 * ```
 */
function SecureSyncIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(secureSyncIconClassName, className),
    label: label,
    children: _g$5 || (_g$5 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: ".64864",
      children: [/*#__PURE__*/jsx("path", {
        d: "M.32 10.3l2.89 4.28 3.31-3.95M23.68 14.74l-2.89-4.28-3.31 3.96M20.76 10.5a8.86 8.86 0 01-14.94 7.73"
      }), /*#__PURE__*/jsx("path", {
        d: "M3.21 14.56A9.1 9.1 0 019.98 3.48a8.8 8.8 0 018.6 2.72M11.9 13.22a.3.3 0 01.28.19.3.3 0 01-.07.33.3.3 0 01-.36.05.3.3 0 01-.15-.34.31.31 0 01.3-.23zm0 0"
      }), /*#__PURE__*/jsx("path", {
        d: "M14.68 11.6H9.11a.17.17 0 00-.16.16v3.53c0 .09.07.16.16.16h5.57c.1 0 .17-.07.17-.16v-3.53a.16.16 0 00-.17-.16zm0 0"
      }), /*#__PURE__*/jsx("path", {
        d: "M9.96 11.6v-1.01c0-.54.2-1.05.57-1.44a1.87 1.87 0 012.74 0c.36.39.56.9.56 1.44v1"
      })]
    }))
  });
}
SecureSyncIcon.displayName = 'SecureSyncIcon';

var _path$7;
const securityBadgeIconClassName = 'bui-security-badge-icon';

/**
 * A component that displays a security badge icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { SecurityBadgeIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <SecurityBadgeIcon/>
 * }
 * ```
 */
function SecurityBadgeIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(securityBadgeIconClassName, className),
    label: label,
    children: _path$7 || (_path$7 = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M4 6.222l8-3.555 8 3.555v5.334c0 4.933-3.413 9.546-8 10.666-4.587-1.12-8-5.733-8-10.666V6.222zm14.222 6.214H12V4.614L5.778 7.378v5.067H12v7.938c3.307-1.023 5.751-4.285 6.222-7.947z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
SecurityBadgeIcon.displayName = 'SecurityBadgeIcon';

var _path$6;
const shoppingBagIconClassName = 'bui-shopping-bag-icon';

/**
 * A component that displays a shopping bag icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ShoppingBagIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ShoppingBagIcon/>
 * }
 * ```
 */
function ShoppingBagIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(shoppingBagIconClassName, className),
    label: label,
    children: _path$6 || (_path$6 = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M8.4 10.2a.6.6 0 00.6-.6V7.2h6v2.4a.6.6 0 101.2 0V7.2h2.56c.15 0 .27.11.28.25l.74 11.76.02.76c0 .57-.47 1.03-1.04 1.03H5.24c-.57 0-1.04-.46-1.04-1.02 0-.26 0-.52.03-.77l.74-11.76a.28.28 0 01.27-.25H7.8v2.4c0 .33.27.6.6.6zM9 6h6a3 3 0 10-6 0zm7.2 0a4.2 4.2 0 00-8.4 0H5.24c-.77 0-1.42.6-1.47 1.37l-.74 11.75c-.02.29-.03.57-.03.86 0 1.23 1 2.22 2.24 2.22h13.52c1.23 0 2.24-1 2.24-2.22 0-.29 0-.57-.03-.86l-.74-11.75A1.48 1.48 0 0018.76 6H16.2z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
ShoppingBagIcon.displayName = 'ShoppingBagIcon';

var _path$5;
const shoppingBasketIconClassName = 'bui-shopping-basket-icon';

/**
 * A component that displays a shopping basket icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ShoppingBasketIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ShoppingBasketIcon/>
 * }
 * ```
 */
function ShoppingBasketIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(shoppingBasketIconClassName, className),
    label: label,
    children: _path$5 || (_path$5 = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M6.65 2.76L4.41 7.8H3.18c-.8 0-1.38.74-1.38 1.6v1c0 .82.54 1.54 1.29 1.6l1.24 7.76c.24 1.1 1.12 1.88 2.16 1.84h11.05c1 .04 1.9-.74 2.12-1.82L20.91 12c.75-.06 1.29-.78 1.29-1.6v-1c0-.86-.58-1.6-1.38-1.6h-1.23l-2.24-5.04a.6.6 0 00-1.1.48l2.03 4.56H5.72l2.03-4.56a.6.6 0 00-1.1-.48zM3.18 9H20.81c.07 0 .19.15.19.4v1c0 .25-.12.4-.18.4H3.18c-.06 0-.18-.15-.18-.4v-1c0-.25.12-.4.18-.4zm2.33 10.53L4.31 12H19.7l-1.2 7.55c-.12.52-.51.87-.93.85H6.46c-.44.02-.83-.33-.95-.87zM12 13.8a.6.6 0 00-.6.6V18a.6.6 0 001.2 0v-3.6a.6.6 0 00-.6-.6zm3.6.5a.6.6 0 111.2.2l-.6 3.6a.6.6 0 11-1.2-.2l.6-3.6zm-7.9-.5a.6.6 0 00-.5.7l.6 3.6a.6.6 0 101.2-.2l-.6-3.6a.6.6 0 00-.7-.5z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
ShoppingBasketIcon.displayName = 'ShoppingBasketIcon';

var _g$4;
const showIconClassName = 'bui-show-icon';

/**
 * A component that displays a visible/show icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ShowIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <ShowIcon/>
 * }
 * ```
 */
function ShowIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(showIconClassName, className),
    label: label,
    children: _g$4 || (_g$4 = /*#__PURE__*/jsxs("g", {
      clipRule: "evenodd",
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5",
      children: [/*#__PURE__*/jsx("path", {
        d: "M12 5.45C7.97 5.38 3.8 8.2 1.18 11.1c-.57.63-.57 1.59 0 2.22 2.56 2.83 6.72 5.7 10.82 5.64 4.1.07 8.26-2.81 10.82-5.64.57-.63.57-1.6 0-2.22C20.2 8.2 16.03 5.39 12 5.45Z"
      }), /*#__PURE__*/jsx("path", {
        d: "M15.75 12.2a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0v0Z"
      })]
    }))
  });
}
ShowIcon.displayName = 'ShowIcon';

var _path$4;
const successIconClassName = 'bui-success-icon';

/**
 * A component that displays a success icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { SuccessIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <SuccessIcon/>
 * }
 * ```
 */
function SuccessIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(successIconClassName, className),
    label: label,
    children: _path$4 || (_path$4 = /*#__PURE__*/jsx("path", {
      d: "M12 21.75a9.75 9.75 0 100-19.5 9.75 9.75 0 000 19.5zm-4.93-9.4a.75.75 0 000 1.06l2.34 2.35a.74.74 0 001.05 0l6.48-6.47a.74.74 0 00-1.05-1.05l-5.95 5.94-1.82-1.82a.75.75 0 00-1.05 0zm0 0",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
SuccessIcon.displayName = 'SuccessIcon';

var _g$3;
const teddyBearIconClassName = 'bui-teddy-bear-icon';

/**
 * A component that displays a teddy bear icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { TeddyBearIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <TeddyBearIcon/>
 * }
 * ```
 */
function TeddyBearIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(teddyBearIconClassName, className),
    label: label,
    children: _g$3 || (_g$3 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.1",
      children: [/*#__PURE__*/jsx("path", {
        d: "M14.05 20.82a5.95 5.95 0 01-4.1 0M10.66 12.7c-1.34-.17-6.12-1.55-6.5.51-.5 2.58 3.9.98 3.9 3.6v.45M12 8.06v1.31M10.69 10.25L12 9.37l1.31.88M10.03 6.75a.22.22 0 01.22.22M9.81 6.97a.22.22 0 01.22-.22M10.03 7.19a.22.22 0 01-.22-.22M10.25 6.97a.22.22 0 01-.22.22M13.97 6.75a.22.22 0 00-.22.22M14.19 6.97a.22.22 0 00-.22-.22M13.97 7.19a.22.22 0 00.22-.22M13.75 6.97a.22.22 0 00.22.22"
      }), /*#__PURE__*/jsx("path", {
        d: "M12 12.88A5.03 5.03 0 1012 2.8a5.03 5.03 0 000 10.06zM10.52 3.03A2.84 2.84 0 107 7.32M13.48 3.03A2.84 2.84 0 1117 7.32M7.84 22.06a2.4 2.4 0 100-4.81 2.4 2.4 0 000 4.81z"
      }), /*#__PURE__*/jsx("path", {
        d: "M13.34 12.7c1.34-.17 6.12-1.55 6.5.51.5 2.58-3.9.98-3.9 3.6v.45"
      }), /*#__PURE__*/jsx("path", {
        d: "M16.16 22.06a2.4 2.4 0 100-4.81 2.4 2.4 0 000 4.81z"
      })]
    }))
  });
}
TeddyBearIcon.displayName = 'TeddyBearIcon';

var _g$2;
const terminalIconClassName = 'bui-terminal-icon';

/**
 * A component that displays a terminal icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { TerminalIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <TerminalIcon/>
 * }
 * ```
 */
function TerminalIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(terminalIconClassName, className),
    label: label,
    children: _g$2 || (_g$2 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [/*#__PURE__*/jsx("path", {
        d: "M19.508 3.877h-15c-.69 0-1.25.56-1.25 1.25v13.75c0 .69.56 1.25 1.25 1.25h15c.69 0 1.25-.56 1.25-1.25V5.128c0-.69-.56-1.25-1.25-1.25ZM3.258 7.628h17.5M12.008 16.378h3.75"
      }), /*#__PURE__*/jsx("path", {
        d: "m8.258 11.377 2.5 2.5-2.5 2.5"
      })]
    }))
  });
}
TerminalIcon.displayName = 'TerminalIcon';

var _path$3;
const timerIconClassName = 'bui-timer-icon';

/**
 * A component that displays a timer icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { TimerIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <TimerIcon/>
 * }
 * ```
 */
function TimerIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(timerIconClassName, className),
    label: label,
    children: _path$3 || (_path$3 = /*#__PURE__*/jsx("path", {
      d: "M12 21.38a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15ZM17 8.25l1.56-1.56M18.25 6.38l.63.62M12 6.38V2.63M13.88 2.63h-3.76M12 14.5l-3.13-3.46",
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }))
  });
}
TimerIcon.displayName = 'TimerIcon';

var _g$1;
const touchUnlockIconClassName = 'bui-touch-unlock-icon';

/**
 * A component that displays a touch unlock icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { TouchUnlockIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <TouchUnlockIcon/>
 * }
 * ```
 */
function TouchUnlockIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(touchUnlockIconClassName, className),
    label: label,
    children: _g$1 || (_g$1 = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: ".66666",
      children: [/*#__PURE__*/jsx("path", {
        d: "M4.5 12.5h-3a1 1 0 01-1-1v-7a1 1 0 011-1h21a1 1 0 011 1v7a1 1 0 01-1 1h-11"
      }), /*#__PURE__*/jsx("path", {
        d: "M21 6l-4 4-2-2M5.65 23.5l-3.8-4.58c-.26-.3-.38-.7-.35-1.1a1.51 1.51 0 011.64-1.36c.4.04.76.24 1.01.54l2.35 2.82V10a1.5 1.5 0 113 0v6.5h3a4 4 0 014 4v3"
      })]
    }))
  });
}
TouchUnlockIcon.displayName = 'TouchUnlockIcon';

var _path$2;
const upChevronIconClassName = 'bui-up-chevron-icon';

/**
 * A component that displays an up chevron icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { UpChevronIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <UpChevronIcon/>
 * }
 * ```
 */
function UpChevronIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(upChevronIconClassName, className),
    label: label,
    children: _path$2 || (_path$2 = /*#__PURE__*/jsx("path", {
      d: "M6 14.3l1.41 1.4L12 11.13l4.59 4.57L18 14.3l-6-6-6 6z",
      fill: "currentColor"
    }))
  });
}
UpChevronIcon.displayName = 'UpChevronIcon';

var _path$1;
const uploadIconClassName = 'bui-upload-icon';

/**
 * A component that displays an upload icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { UploadIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <UploadIcon/>
 * }
 * ```
 */
function UploadIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(uploadIconClassName, className),
    label: label,
    children: _path$1 || (_path$1 = /*#__PURE__*/jsx("path", {
      clipRule: "evenodd",
      d: "M15 16.5v-6h4l-7-7-7 7h4v6h6zM12 6.33l2.17 2.17H13v6h-2v-6H9.83L12 6.33zm7 14.17v-2H5v2h14z",
      fill: "currentColor",
      fillRule: "evenodd"
    }))
  });
}
UploadIcon.displayName = 'UploadIcon';

var _g;
const userIconClassName = 'bui-user-icon';

/**
 * A component that displays a user icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { UserIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <UserIcon/>
 * }
 * ```
 */
function UserIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(userIconClassName, className),
    label: label,
    children: _g || (_g = /*#__PURE__*/jsxs("g", {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [/*#__PURE__*/jsx("path", {
        d: "M8 10a4 4 0 1 0 8 0 4 4 0 0 0-8 0v0ZM18 19c-.786-.95-1.721-1.705-2.75-2.22A7.247 7.247 0 0 0 12 16c-1.115 0-2.22.265-3.25.78C7.722 17.295 6.787 18.05 6 19"
      }), /*#__PURE__*/jsx("path", {
        d: "M2.625 12a9.375 9.375 0 1 0 18.75 0 9.375 9.375 0 0 0-18.75 0v0Z"
      })]
    }))
  });
}
UserIcon.displayName = 'UserIcon';

var _path;
const warningIconClassName = 'bui-warning-icon';

/**
 * A component that displays a warning icon.
 * The component also offers an optional invisible label for screen readers.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { WarningIcon } from '@thisisbud/bui-react-icons'
 *
 * function Example() {
 *   return <WarningIcon/>
 * }
 * ```
 */
function WarningIcon(props) {
  const {
    className,
    label
  } = props;
  return /*#__PURE__*/jsx(Icon, {
    className: classnames(warningIconClassName, className),
    label: label,
    children: _path || (_path = /*#__PURE__*/jsx("path", {
      d: "M12 2a10.2 10.2 0 0 0-9.28 6.32A9.87 9.87 0 0 0 2 12.18 9.82 9.82 0 0 0 11.83 22H12a10.1 10.1 0 0 0 10-10.17A9.8 9.8 0 0 0 12 2Zm-1.25 13.78A1.23 1.23 0 0 1 12 14.5a1.28 1.28 0 0 1 1.19 1.71 1.23 1.23 0 0 1-1.15.79 1.28 1.28 0 0 1-1.29-1.22Zm.42-3.33v-5a.83.83 0 0 1 1.66 0v5a.83.83 0 0 1-1.66 0Z",
      fill: "currentColor"
    }))
  });
}
WarningIcon.displayName = 'WarningIcon';

export { ApprovedIcon, ArrowDownIcon, ArrowDownUpIcon, ArrowUpIcon, AzureIcon, BanknoteIcon, BinIcon, BurgerIcon, BusIcon, CalloutIcon, CashIcon, CashWadIcon, CheckmarkIcon, CloseIcon, ContractHandshakeIcon, CopyIcon, CreditCardIcon, DataDownloadIcon, DatabaseIcon, DocumentWithBookmarkIcon, DownChevronIcon, DownloadIcon, ErrorIcon, ExchangeIcon, FileIcon, FlagIcon, GeneralIcon, GoogleIcon, HeartbeatIcon, HideIcon, HideTableIcon, HolidayIcon, HouseIcon, InfoIcon, LeftChevronIcon, LetterIcon, LogoutIcon, PadlockBadgeIcon, PdfFileIcon, PencilIcon, PiggyBankIcon, PlusIcon, PriceTagIcon, QuestionmarkCircleIcon, RightChevronIcon, SearchIcon, SecureSyncIcon, SecurityBadgeIcon, ShoppingBagIcon, ShoppingBasketIcon, ShowIcon, SuccessIcon, TeddyBearIcon, TerminalIcon, TimerIcon, TouchUnlockIcon, UpChevronIcon, UploadIcon, UserIcon, WarningIcon, approvedIconClassName, arrowDownIconClassName, arrowDownUpIconClassName, arrowUpIconClassName, azureIconClassName, banknoteIconClassName, binIconClassName, burgerIconClassName, busIconClassName, calloutIconClassName, cashIconClassName, cashWadIconClassName, checkmarkIconClassName, closeIconClassName, contractHandshakeIconClassName, copyIconClassName, creditCardIconClassName, dataDownloadIconClassName, databaseIconClassName, documentWithBookmarkIconClassName, downChevronIconClassName, downloadIconClassName, errorIconClassName, exchangeIconClassName, fileIconClassName, flagIconClassName, generalIconClassName, googleIconClassName, heartbeatIconClassName, hideIconClassName, hideTableIconClassName, holidayIconClassName, houseIconClassName, iconClassName, iconSize, infoIconClassName, leftChevronIconClassName, letterIconClassName, logoutIconClassName, padlockBadgeIconClassName, pdfFileIconClassName, pencilIconClassName, piggyBankIconClassName, plusIconClassName, priceTagIconClassName, questionmarkCircleIconClassName, rightChevronIconClassName, searchIconClassName, secureSyncIconClassName, securityBadgeIconClassName, shoppingBagIconClassName, shoppingBasketIconClassName, showIconClassName, successIconClassName, teddyBearIconClassName, terminalIconClassName, timerIconClassName, touchUnlockIconClassName, upChevronIconClassName, uploadIconClassName, userIconClassName, warningIconClassName };
